/**
 * Which stored config the JSON editor opens, and what it needs to save safely.
 *
 * The authored-draft-beats-committed-file rule decides what a user is actually
 * editing, so getting it wrong means silently editing — and then overwriting —
 * the wrong thing. Kept out of the component file so Fast Refresh can preserve
 * editor state (react-doctor/only-export-components).
 */
export interface EditorPanelProps {
  supplierName: string;
  initialText: string;
  source: 'authored' | 'empty';
  status: string | null;
  /**
   * `updatedAt` of the authored row this buffer was loaded from, sent back on save
   * so the server can refuse a write built on a stale read. Undefined when there
   * is no stored row yet.
   */
  expectedUpdatedAt?: string;
}

/** What `useSd849Config` returns, narrowed to what the editor needs. */
export interface Sd849ConfigData {
  authored?: {
    config: Record<string, unknown>;
    status: string | null;
    updatedAt?: string;
  } | null;
}

/**
 * Derive the editor's starting point from a fetched config. There is one source
 * now — the DB row — so a supplier either has a config to edit or is starting
 * from an empty object.
 */
export function parserEditorPropsFrom(
  data: Sd849ConfigData,
): Pick<
  EditorPanelProps,
  'initialText' | 'source' | 'status' | 'expectedUpdatedAt'
> {
  const authored = data.authored ?? null;
  return {
    initialText: JSON.stringify(authored?.config ?? {}, null, 2),
    source: authored ? 'authored' : 'empty',
    status: authored?.status ?? null,
    expectedUpdatedAt: authored?.updatedAt,
  };
}

/**
 * Whether the loaded row is the one the live parser reads.
 *
 * A save sends no `status` and `saveConfig` deliberately leaves an existing one
 * alone (an absent status must not reset a live config to draft), then drops the
 * runtime cache — so saving an `active` row changes live parsing immediately, with
 * no deploy and no promotion step. Every surface that labels or guards a save asks
 * this one question, so they cannot disagree about which saves are live.
 */
export function isLiveParserConfig(status: string | null | undefined): boolean {
  return status === 'active';
}
